// src/components/UI.js
import React, { useState, useEffect, useCallback } from 'react';

// Toast notification
let toastTimer;
export function Toast({ message, onClose }) {
  useEffect(() => {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(onClose, 2800);
    return () => clearTimeout(toastTimer);
  }, [message, onClose]);
  if (!message) return null;
  return <div className="toast">{message}</div>;
}

export function useToast() {
  const [msg, setMsg] = useState('');
  const show = useCallback((m) => setMsg(m), []);
  const clear = useCallback(() => setMsg(''), []);
  return { msg, show, clear };
}

// Status badge — color comes from the per-client configurable lead_statuses
// table (Settings > Lead Pipeline), passed in as `statuses` by the page that
// fetched it via useLeadLookups(). Falls back to a neutral gray for a status
// name that isn't in the list (e.g. still loading, or since renamed/removed).
export function StatusBadge({ status, statuses = [] }) {
  const color = statuses.find(s => s.name === status)?.color || '#9ca3af';
  return <span className="status-badge" style={{ background: color + '22', color }}>{status}</span>;
}

// Source badge — same pattern, driven by lead_sources.
export function SourceBadge({ source, sources = [] }) {
  const color = sources.find(s => s.name === source)?.color || '#6b7280';
  return <span className="source-badge" style={{ background: color + '22', color }}>{source}</span>;
}

// Avatar
export function Avatar({ initials, color = 'blue', size = '' }) {
  return <div className={`avatar ${color} ${size}`}>{initials}</div>;
}

// Toggle
export function Toggle({ value, onChange }) {
  return (
    <button
      className={`toggle ${value ? 'on' : ''}`}
      onClick={() => onChange(!value)}
      type="button"
      aria-checked={value}
      role="switch"
    />
  );
}

// Modal
export function Modal({ open, onClose, title, children, width = 560 }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width }}>
        <div className="modal-title">
          <span>{title}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Empty state
export function EmptyState({ icon = 'ti-inbox', text }) {
  return (
    <div className="empty-state">
      <i className={`ti ${icon}`} />
      <p>{text}</p>
    </div>
  );
}

// Format date
export function formatDate(str) {
  if (!str) return '—';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

export function getDateStr(offset) {
  const d = new Date(); d.setDate(d.getDate() + offset);
  return d.toISOString().split('T')[0];
}
